/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { useScreenVoice } from "./useScreenVoice";
import { SAFETY_QUESTIONS, TEST_MAP } from "./GSA_CONSTANTS";
import { MYRA_MESSAGES, resolveMessage } from "./GSA_MYRA_MESSAGES";
import styles from "./SelfAssessment.module.css";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";
import { generatePayloadForScreen } from "./generatePayloadForScreen";
import MyraChatService from "../voice-agent/MyraChatService";

let _ssId = 80000;
function nextSsId() {
  return ++_ssId;
}
let _historyId = 0;
function nextId() {
  return ++_historyId;
}

export default function SafetyScreeningScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, onHardStop, readOnly = false, entry = null, showQuestionNuggetMessage, setPlaceholderText }) {
  const problemArea = assessmentData.problemArea;
  const safetyData = SAFETY_QUESTIONS[problemArea];

  const [ready, setReady] = useState(false);
  const [outcomeText, setOutcomeText] = useState("");

  // ── Screen status ──
  // 'awaiting' → user hasn't replied yet OR Beacon/Claude is following up
  // 'advisory' → SAFETY_ADVISORY received, user chooses Continue or Leave
  // 'stopped'  → SAFETY_STOP received or user chose Leave on advisory
  const [screenStatus, setScreenStatus] = useState("awaiting");
  const screenStatusRef = useRef("awaiting");
  const safetyContinueLockedRef = useRef(false);

  useEffect(() => {
    screenStatusRef.current = screenStatus;
  }, [screenStatus]);

  // ── Push setup beat + question + nugget on mount; set placeholder ──
  useEffect(() => {
    if (!safetyData) return;
    let cancelled = false;
    async function init() {
      if (!readOnly) {
        await showMyraMessage(MYRA_MESSAGES.safety.SAFETY_SETUP_BEAT, 800);
        await showQuestionNuggetMessage(MYRA_MESSAGES.safety.SAFETY_QUESTION, MYRA_MESSAGES.safety.SAFETY_NUGGET_BY_AREA[problemArea], 900);
      }
      if (!cancelled) setReady(true);
    }
    init();
    if (setPlaceholderText && MYRA_MESSAGES.safety.placeholder) {
      setPlaceholderText(MYRA_MESSAGES.safety.placeholder);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Voice flow setup ──
  useEffect(() => {
    window.currentFlow = "flow_safety";
    VoiceService._currentFlow = "flow_safety";
    if (window.mayraCode) {
      VoiceService.startFlowWithoutAnnouncement("flow_safety");
    }
  }, []);

  // ── SAFETY_CONTINUE handler — clear path, go to Movement Test 1 demo ──
  const handleSafetyContinue = useCallback(() => {
    if (safetyContinueLockedRef.current) return;
    safetyContinueLockedRef.current = true;
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
        value: 1.0,
        currency: "USD",
      });
    }
    const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
    const test = TEST_MAP[subtype];
    const testName = test?.name || assessmentData.testName || assessmentData.rom_test1_name || "";
    pushToHistory({ type: "divider", id: nextId() });
    pushToHistory({ type: "section-header", label: "MOVEMENT TEST 1 - DEMO VIDEO", id: nextId() });
    const testTransitionTemplate = MYRA_MESSAGES.preNprs?.transitionToTest;
    if (testTransitionTemplate) {
      const testTransition = resolveMessage(testTransitionTemplate, { testName });
      setTimeout(async () => {
        await showMyraMessage(testTransition, 800);
      }, 10);
    }
    onContinue({
      safetyScreenResult: "CLEAR",
      safetyOutcome: "CONTINUE",
      completed_safety_screen: true,
      selected_pain_location: true,
      // balance is now captured on Personalize; defaulted here for payload compatibility
      balance: "steady",
      started_safety_screen: true,
      safety_condition_selected: false,
    });
  }, [assessmentData, pushToHistory, showMyraMessage, onContinue]);

  // ── Advisory: user chose Continue ──
  const handleAdvisoryContinue = useCallback(() => {
    if (safetyContinueLockedRef.current) return;
    safetyContinueLockedRef.current = true;
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD",
        value: 1.0,
        currency: "USD",
      });
    }
    window.notOpenQNA = false;
    const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
    const test = TEST_MAP[subtype];
    const testName = test?.name || assessmentData.testName || assessmentData.rom_test1_name || "";
    pushToHistory({ type: "divider", id: nextId() });
    pushToHistory({ type: "section-header", label: "MOVEMENT TEST 1 - DEMO VIDEO", id: nextId() });
    const testTransitionTemplate = MYRA_MESSAGES.preNprs?.transitionToTest;
    if (testTransitionTemplate) {
      const testTransition = resolveMessage(testTransitionTemplate, { testName });
      setTimeout(async () => {
        await showMyraMessage(testTransition, 800);
      }, 10);
    }
    onContinue({
      safetyScreenResult: "CLEAR",
      safetyOutcome: "ADVISORY_CONTINUE",
      completed_safety_screen: true,
      selected_pain_location: true,
      balance: "steady",
      started_safety_screen: true,
      safety_condition_selected: true,
    });
  }, [assessmentData, pushToHistory, showMyraMessage, onContinue]);

  // ── Advisory: user chose Leave ──
  const handleAdvisoryLeave = useCallback(() => {
    setScreenStatus("stopped");
    screenStatusRef.current = "stopped";

    pushToHistory({
      type: "frozen-widget",
      widgetType: "safety_advisory_card",
      id: nextSsId(),
      data: { detail: outcomeText, choice: "leave" },
    });

    const leaveMsg = MYRA_MESSAGES.safetyOutcome?.advisoryLeave || "No problem at all. When you're ready to give it a try, I'll be right here.";
    showMyraMessage(leaveMsg, 800);
    setTimeout(() => {
      window.location.href = "/?gsa=v2";
    }, 3000);
  }, [outcomeText, pushToHistory, showMyraMessage]);

  // ── Voice/chat command registration ──
  useScreenVoice({
    screenName: "SafetyScreeningScreen",
    trackerName: "safety_screening",
    flowName: "flow_safety",
    payloadKey: "SafetyScreeningScreen",

    commands: {
      MOVE_ON: () => {
        handleSafetyContinue();
      },
      SAFETY_CONTINUE: () => {
        handleSafetyContinue();
      },

      SAFETY_STOP: (value) => {
        if (window._safetyStopExecuted) return;

        window._safetyStopExecuted = true;

        setOutcomeText(value || "");
        setScreenStatus("stopped");
        screenStatusRef.current = "stopped";

        pushToHistory({
          type: "frozen-widget",
          widgetType: "safety_stop_card",
          id: nextSsId(),
          data: {
            detail: value || "",
          },
        });

        const followUp = MYRA_MESSAGES.safetyOutcome?.stoppedFollowUp || "I know this isn't the news you were hoping for. If you have any questions, I'm right here.";
        showMyraMessage(followUp, 800);

        setTimeout(() => {
          window._safetyStopExecuted = false;
        }, 2000);
      },
      SAFETY_ADVISORY: (value) => {
        setOutcomeText(value || "");
        setScreenStatus("advisory");
        screenStatusRef.current = "advisory";
      },

      SELECT_SAFETY_CONDITION: async (value, speech, intent, extracted, actionValue, actions) => {
        setTimeout(async () => {
          await showMyraMessage(MYRA_MESSAGES.safety?.followUpIntro || "Thanks for letting me know. I have a couple of follow-up questions to make sure it's safe to continue.", 800);
        }, 10);

        // Extract selected conditions directly from backend actions
        const selectedConditions = (actions || [])
          .filter((a) => a.action === "SELECT_SAFETY_CONDITION")
          .map((a) => a.value)
          .filter(Boolean);

        // fallback if backend sends single value only
        if (!selectedConditions.length && value) {
          selectedConditions.push(value);
        }

        const payloadWithSelections = generatePayloadForScreen("SafetyScreeningScreen", {
          ...assessmentData,
          safetySelections: selectedConditions,
          safetySelectionIds: selectedConditions,
        });

        if (VoiceService?._ws?.readyState === WebSocket.OPEN) {
          VoiceService.sendMidJourneyData(payloadWithSelections);
        }

        setScreenStatus("waiting");
        screenStatusRef.current = "waiting";

        if (MyraChatService) {
          window.notOpenQNA = true;
          window.safetySelections = selectedConditions;
          window.safetySelectionIds = selectedConditions;

          try {
            const symptomMessage = `I've these symptoms: ${selectedConditions.join(", ")}. Please ask follow-up questions to evaluate safety.`;

            const newHistory = [
              {
                role: "user",
                content: symptomMessage,
              },
            ];

            const response = await MyraChatService.sendMessage(symptomMessage, window.trSessionId, newHistory, 0, payloadWithSelections, "SafetyScreeningScreen");

            const responseText = response?.response || response?.speech || "I need to ask a few follow-up questions to understand your situation better.";

            await showMyraMessage(responseText, 900);
          } catch (err) {
            console.error("Failed to send symptom message:", err);
          }
        }

        VoiceService.handleSafetyScreenFollowUp();
      },
    },

    exitReason: "safety_screening_completed",
  });

  // ── No safety data fallback ──
  useEffect(() => {
    if (!safetyData) {
      onContinue({
        safetyScreenResult: "CLEAR",
        safetyOutcome: "CONTINUE",
        completed_safety_screen: true,
        selected_pain_location: true,
        started_safety_screen: true,
        safety_condition_selected: false,
        balance: "steady",
      });
    }
  }, [safetyData, onContinue]);

  if (!safetyData) return null;
  if (!ready) return null;

  // ── Stopped: no live UI (stop card + follow-up are in history) ──
  if (screenStatus === "stopped") return null;

  // ── Advisory: show card with Continue / Leave ──
  if (screenStatus === "advisory") {
    return (
      <div className={styles.inlineUi}>
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #f59e0b",
            borderRadius: "0.75rem",
            padding: "1.25rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⚠️</div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#1f2937",
              marginBottom: "0.75rem",
            }}
          >
            {MYRA_MESSAGES.safetyOutcome?.advisoryHeading || "Something to consider before continuing"}
          </div>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#4b5563",
              lineHeight: 1.5,
              marginBottom: "1.25rem",
            }}
          >
            {outcomeText}
          </p>

          <button
            style={{
              width: "100%",
              background: "#0d9488",
              color: "white",
              fontWeight: "bold",
              fontSize: "16px",
              padding: "0.875rem",
              borderRadius: "0.5rem",
              border: "none",
              cursor: "pointer",
              marginBottom: "0.625rem",
            }}
            onClick={handleAdvisoryContinue}
          >
            Continue with assessment
          </button>

          <button
            style={{
              width: "100%",
              background: "transparent",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: "15px",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
            }}
            onClick={handleAdvisoryLeave}
          >
            I'll come back later
          </button>
        </div>
      </div>
    );
  }

  // ── Awaiting: no live UI — question + nugget bubble is in conversation history,
  //    user replies through the existing chat composer. Beacon parses "none" /
  //    "no" / "nope" / etc. and routes to SAFETY_CONTINUE; anything else is
  //    handed to Claude, which conducts the follow-up conversation and
  //    returns SAFETY_CONTINUE / SAFETY_ADVISORY / SAFETY_STOP. ──
  return null;
}
